import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { USERS_API_URL } from '../../config/apiConfig';

const ScreenManagementScreen = () => {
  const { token } = useAuth();
  const [screens, setScreens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchScreens();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchScreens = async () => {
    try {
      const response = await fetch(`${USERS_API_URL}/screens`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setScreens(data.screens || []);
      } else {
        setError('Failed to fetch screens');
      }
    } catch (error) {
      console.error('Error fetching screens:', error);
      setError('Failed to fetch screens');
    } finally {
      setLoading(false);
    }
  };

  const updateScreenPublicStatus = async (screenId, isPublic) => {
    // Show confirmation dialog when making a screen public
    if (isPublic) {
      const confirmed = window.confirm(
        'Making this screen public will automatically remove it from all user roles since public screens don\'t require authentication.\n\n' +
        'All users will lose their role-based access to this screen, and it will become accessible to everyone.\n\n' +
        'Are you sure you want to continue?'
      );
      
      if (!confirmed) {
        return;
      }
    }

    try {
      const response = await fetch(`${USERS_API_URL}/screens/${screenId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ is_public: isPublic })
      });

      if (response.ok) {
        const data = await response.json();
        
        if (data.permissions_removed) {
          setSuccess(
            'Screen marked as public successfully. All role permissions for this screen have been removed since public screens don\'t require authentication.'
          );
        } else {
          setSuccess(`Screen ${isPublic ? 'marked as public' : 'marked as private'} successfully`);
        }
        
        fetchScreens(); // Refresh the list
        setTimeout(() => setSuccess(''), 5000); // Longer timeout for longer message
      } else {
        const data = await response.json();
        setError(data.error || 'Failed to update screen');
      }
    } catch (error) {
      console.error('Error updating screen:', error);
      setError('Failed to update screen');
    }
  };

  if (loading) {
    return <div>Loading screens...</div>;
  }

  return (
    <div style={{ padding: '2rem' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h1>Screen Management</h1>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          color: '#721c24',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {success && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#d4edda',
          border: '1px solid #c3e6cb',
          borderRadius: '4px',
          color: '#155724',
          marginBottom: '1rem'
        }}>
          {success}
        </div>
      )}

      <div style={{
        background: '#f8f9fa',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '2rem'
      }}>
        <h3>About Public Screens</h3>
        <p>
          Public screens can be accessed by anyone without authentication. 
          Use this feature carefully and only mark screens as public if they contain 
          information that should be accessible to the general public.
        </p>
        <div style={{
          marginTop: '1rem',
          padding: '0.75rem',
          background: '#fff3cd',
          border: '1px solid #ffeaa7',
          borderRadius: '4px',
          color: '#856404'
        }}>
          <strong>Important:</strong> When you make a screen public, all role-based permissions 
          for that screen will be automatically removed from all user roles, since public screens 
          don't require authentication or authorization.
        </div>
      </div>

      <div style={{
        border: '1px solid #ddd',
        borderRadius: '8px',
        overflow: 'hidden'
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#f8f9fa' }}>
              <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Screen Name</th>
              <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Path</th>
              <th style={{ padding: '1rem', textAlign: 'left', borderBottom: '1px solid #ddd' }}>Description</th>
              <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Public</th>
              <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Status</th>
              <th style={{ padding: '1rem', textAlign: 'center', borderBottom: '1px solid #ddd' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {screens.map((screen) => (
              <tr key={screen.screen_id} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: '1rem' }}>{screen.screen_name}</td>
                <td style={{ padding: '1rem', fontFamily: 'monospace', fontSize: '0.9rem' }}>
                  {screen.screen_path}
                </td>
                <td style={{ padding: '1rem', color: '#666' }}>
                  {screen.description || 'No description'}
                </td>
                <td style={{ padding: '1rem', textAlign: 'center' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    fontWeight: 'bold',
                    backgroundColor: screen.is_public ? '#28a745' : '#6c757d',
                    color: 'white'
                  }}>
                    {screen.is_public ? 'PUBLIC' : 'PRIVATE'}
                  </span>
                </td>
                <td style={{ padding: '1rem', textAlign: 'center' }}>
                  <span style={{
                    padding: '0.25rem 0.5rem',
                    borderRadius: '4px',
                    fontSize: '0.8rem',
                    backgroundColor: screen.is_active ? '#e8f5e8' : '#ffe6e6',
                    color: screen.is_active ? '#28a745' : '#dc3545'
                  }}>
                    {screen.is_active ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td style={{ padding: '1rem', textAlign: 'center' }}>
                  {screen.is_active && (
                    <button
                      onClick={() => updateScreenPublicStatus(screen.screen_id, !screen.is_public)}
                      style={{
                        padding: '0.25rem 0.5rem',
                        backgroundColor: screen.is_public ? '#dc3545' : '#28a745',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '0.8rem'
                      }}
                    >
                      {screen.is_public ? 'Make Private' : 'Make Public'}
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {screens.length === 0 && (
        <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
          No screens found.
        </div>
      )}
    </div>
  );
};

export default ScreenManagementScreen;
